import { useState, useEffect } from "react";
import classNames from "classnames";
import CiroCreateableTextInput from "../../../shared/CiroCreateableTextInput";
import CiroFilterModalDropdownSection from "../../../shared/CiroFilter/CiroFilterModalDropdownSection";
import { InformationCircleIcon, PencilIcon, PlusIcon } from "@heroicons/react/24/outline";
import CiroTooltipContainer from "../../../shared/CiroTooltipContainer";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import BulkCompanyEntryModal from "../BulkCompanyEntryModal";

const CuratedSearchFilterCurrentCompanySection = ({
  currentCompanyFilter,
  setCurrentCompanyFilter,
  excludedCompanyFilter,
  setExcludedCompanyFilter,
  defaultOpen = false,
}: {
  currentCompanyFilter: { label: string }[];
  setCurrentCompanyFilter: (v: { label: string }[]) => void;
  excludedCompanyFilter?: { label: string }[];
  setExcludedCompanyFilter?: (v: { label: string }[]) => void;
  defaultOpen?: boolean;
}) => {
  const [isBulkEntryModalOpen, setIsBulkEntryModalOpen] = useState(false);
  const [companiesText, setCompaniesText] = useState(currentCompanyFilter.map(company => company.label).join("\n"));
  const numberOfCompanies = currentCompanyFilter.length;

  // Sync companiesText with currentCompanyFilter when it changes externally
  useEffect(() => {
    setCompaniesText(currentCompanyFilter.map(company => company.label).join("\n"));
  }, [currentCompanyFilter]);

  const handleBulkCompanySave = () => {
    // Combine existing companies with new ones, remove duplicates
    const companyList = companiesText
      ? companiesText
          .split("\n")
          .map((company) => company.trim())
          .filter(Boolean)
          .map((company) => ({ label: company }))
      : [];
    const uniqueCompanies = companyList.filter(
      (company, index, self) =>
        index === self.findIndex((c) => c.label === company.label),
    );
    setCurrentCompanyFilter(uniqueCompanies);
  };

  return (
    <div>
      <CiroFilterModalDropdownSection
        defaultOpen={defaultOpen}
        numFiltersApplied={
          currentCompanyFilter.length + (excludedCompanyFilter?.length ?? 0)
        }
        previews={[
          ...currentCompanyFilter.map((c) => c.label),
          ...(excludedCompanyFilter?.map((c) => `Not: ${c.label}`) ?? []),
        ]}
        sectionTitle={"Current company"}
        filterOptions={[
          {
            label: (
              <div
                className={classNames(
                  "flex",
                  "flex-row",
                  "gap-1",
                  "items-center",
                  "mb-1",
                )}
              >
                <div className={classNames("text-sm")}>
                  Current company includes any of:
                </div>
              </div>
            ),
            filter: (
              <div
                className={classNames("space-y-2", "flex", "flex-col", "gap-2")}
              >
                <CiroCreateableTextInput
                  containerWidthClass={classNames("w-full")}
                  value={currentCompanyFilter.map((c) => c.label)}
                  placeholder={"Enter one or more companies"}
                  setValue={(newValue) => {
                    // Support copy-pasting comma-separated or newline-separated list of company names
                    let splitValues: string[] = newValue.flatMap((val) =>
                      val.includes(",") || val.includes("\n")
                        ? val
                            .split(/[,\n]/)
                            .map((item) => item.trim())
                            .filter(Boolean)
                        : val,
                    );
                    setCurrentCompanyFilter(
                      splitValues.map((label) => ({ label })) as {
                        label: string;
                      }[],
                    );
                  }}
                  maxDisplayedOptions={100}
                />
                <div className={classNames("flex", "justify-end")}>
                  <CiroButton
                    style={CiroButtonStyleEnum.UNSTYLED}
                    onClick={() => setIsBulkEntryModalOpen(true)}
                    analyticsField={"Bulk add companies in curated search"}
                  >
                    <div
                      className={classNames(
                        "flex",
                        "flex-row",
                        "gap-1",
                        "items-center",
                        "text-neutral-500",
                        "text-sm",
                        "hover:text-orange-500",
                      )}
                    >
                      {numberOfCompanies === 0 ? (
                        <PlusIcon className={classNames("h-4", "w-4")} />
                      ) : (
                        <PencilIcon className={classNames("h-4", "w-4")} />
                      )}
                      Bulk {numberOfCompanies === 0 ? "add" : "edit"} companies
                    </div>
                  </CiroButton>
                </div>
              </div>
            ),
          },
          ...(excludedCompanyFilter !== undefined
            ? [
                {
                  label: (
                    <div
                      className={classNames(
                        "flex",
                        "flex-row",
                        "gap-1",
                        "items-center",
                        "mb-1",
                        "mt-4",
                      )}
                    >
                      <div className={classNames("w-5", "text-neutral-500")}>
                        <CiroTooltipContainer
                          tooltip={"Try pasting a comma-separated list"}
                        >
                          <InformationCircleIcon />
                        </CiroTooltipContainer>
                      </div>
                      <div className={classNames("text-sm")}>
                        Exclude these companies:
                      </div>
                    </div>
                  ),
                  filter: (
                    <CiroCreateableTextInput
                      containerWidthClass={classNames("w-full")}
                      value={excludedCompanyFilter?.map((c) => c.label)}
                      placeholder={"Enter one or more companies to exclude"}
                      setValue={(newValue) => {
                        // Support copy-pasting comma-separated or newline-separated list of company names
                        let splitValues: string[] = newValue.flatMap((val) =>
                          val.includes(",") || val.includes("\n")
                            ? val
                                .split(/[,\n]/)
                                .map((item) => item.trim())
                                .filter(Boolean)
                            : val,
                        );
                        if (setExcludedCompanyFilter) {
                          setExcludedCompanyFilter(
                            splitValues.map((label) => ({ label })) as {
                              label: string;
                            }[],
                          );
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <BulkCompanyEntryModal
        isOpen={isBulkEntryModalOpen}
        onClose={() => setIsBulkEntryModalOpen(false)}
        onSave={handleBulkCompanySave}
        companiesText={companiesText}
        setCompaniesText={setCompaniesText}
      />
    </div>
  );
};

export default CuratedSearchFilterCurrentCompanySection;
